import { Col, Container, Row, Spinner, Button } from "react-bootstrap";
import CustomBreadcrumbs from "../navigation/breadcrumbs/CustomBreadcrumbs";
import Searchbar from "../navigation/navbar/Searchbar";
import CalendarButton from "./CalendarButton";
import FilterButton from "./filters/FilterButton";
import { FilterValues } from "./filters/FiltersDropdown";
import { BsBicycle, BsDownload, BsCheckSquare, BsXLg } from "react-icons/bs";
import SessionTile from "./SessionTile";
import SessionPagination from "./SessionPagination";
import BulkDownloadModal, { BulkSelectedSession } from "./BulkDownloadModal";
import { requestWithAuthorization } from "../../network/apiRequests";
import { useEffect, useState, useRef } from "react";

export default function SessionsComponent() {
  const SESSIONS_PER_PAGE = 20;

  // State for pagination, filters, and search
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [recordings, setRecordings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterValues>({});
  const [searchTerm, setSearchTerm] = useState("");

  // Selection mode state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);

  /**
   * Fetches pages of recordings until we collect targetUniqueCount unique recIds (or run out of recordings)
   */
  async function fetchUniqueRecordings(page = 1, filters: FilterValues = activeFilters, search = searchTerm) {
    let currentFetchOffset = (page - 1) * SESSIONS_PER_PAGE;
    let accumulatedRecordings: any[] = [];
    const uniqueRecIds = new Set<string>();
    let totalItemsFromApi = 0;

    // Fetch batches until we have SESSIONS_PER_PAGE unique recIds or no more items
    while (uniqueRecIds.size < SESSIONS_PER_PAGE) {
      let queryParams = `limit=${SESSIONS_PER_PAGE}&offset=${currentFetchOffset}`;

      if (search && search.trim() !== "") {
        queryParams += `&name=${encodeURIComponent(search.trim())}`;
      }
      if (filters.minDistance !== undefined) {
        queryParams += `&min_distance=${filters.minDistance}`;
      }
      if (filters.maxDistance !== undefined) {
        queryParams += `&max_distance=${filters.maxDistance}`;
      }
      if (filters.minDuration !== undefined) {
        queryParams += `&min_duration=${filters.minDuration}`;
      }
      if (filters.maxDuration !== undefined) {
        queryParams += `&max_duration=${filters.maxDuration}`;
      }
      if (filters.startDate) {
        const startDateStr = filters.startDate.toISOString().split('T')[0];
        queryParams += `&startdate=${startDateStr}`;
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setDate(endDate.getDate() + 1);
        const endDateStr = endDate.toISOString().split('T')[0];
        queryParams += `&enddate=${endDateStr}`;
      }

      try {
        const response = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/recordings?${queryParams}`
        );

        if (response.status !== 200 || !response.data) {
          break;
        }

        const batch = response.data.recordings || [];
        totalItemsFromApi = response.data.total || totalItemsFromApi;

        if (batch.length === 0) {
          break; // No more items on backend
        }

        for (const item of batch) {
          accumulatedRecordings.push(item);
          if (item.recId) {
            uniqueRecIds.add(item.recId);
          }
          if (uniqueRecIds.size >= SESSIONS_PER_PAGE) {
            break;
          }
        }

        currentFetchOffset += batch.length;
        if (currentFetchOffset >= totalItemsFromApi) {
          break;
        }
      } catch (error) {
        console.error("Error fetching recordings batch:", error);
        break;
      }
    }

    return {
      total: totalItemsFromApi,
      recordings: accumulatedRecordings
    };
  }

  // Reference to store the debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle search with debouncing
  const handleSearch = (term: string) => {
    setSearchTerm(term);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      setLoading(true);

      fetchUniqueRecordings(1, activeFilters, term)
        .then((result) => {
          if (!result) return;
          setTotalCount(result.total);
          const recs = result.recordings;
          if (recs.length > 0) {
            recs.sort((a: any, b: any) => new Date(b.start).getTime() - new Date(a.start).getTime());
            setRecordings(recs);
          } else {
            setRecordings([]);
          }
        })
        .finally(() => setLoading(false));
    }, 300);
  };

  // Handle filter changes with debouncing
  const handleFilterChange = (filters: FilterValues) => {
    setActiveFilters(filters);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      setLoading(true);

      fetchUniqueRecordings(1, filters, searchTerm)
        .then((result) => {
          if (!result) return;
          setTotalCount(result.total);
          const recs = result.recordings;
          if (recs.length > 0) {
            recs.sort((a: any, b: any) => new Date(b.start).getTime() - new Date(a.start).getTime());
            setRecordings(recs);
          } else {
            setRecordings([]);
          }
        })
        .finally(() => setLoading(false));
    }, 300);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setLoading(true);
    fetchUniqueRecordings(newPage, activeFilters, searchTerm)
      .then((result) => {
        if (!result) return;
        setTotalCount(result.total);
        const recs = result.recordings;
        if (recs.length > 0) {
          recs.sort((a: any, b: any) => new Date(b.start).getTime() - new Date(a.start).getTime());
          setRecordings(recs);
        } else {
          setRecordings([]);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchUniqueRecordings(currentPage, activeFilters, searchTerm)
      .then((result) => {
        if (!result) return;
        setTotalCount(result.total);
        const recs = result.recordings;
        if (recs.length > 0) {
          recs.sort((a: any, b: any) => new Date(b.start).getTime() - new Date(a.start).getTime());
          setRecordings(recs);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  /** Deduplicate recordings by recId so multiple entries sharing recId form 1 session */
  const uniqueSessionsMap = new Map<string, any>();
  recordings.forEach((recording: any) => {
    const recId = recording.recId;
    if (!uniqueSessionsMap.has(recId)) {
      uniqueSessionsMap.set(recId, {
        id: recId,
        name: recording.recordingName,
        date: recording.start,
        start: recording.start,
        end: recording.end,
        sensorId: recording.sensorId,
        icon: <BsBicycle />,
      });
    }
  });
  const sessions = Array.from(uniqueSessionsMap.values());

  // ----- Selection mode handlers -----
  const handleEnterSelectMode = () => {
    setIsSelectMode(true);
    setSelectedSessionIds(new Set());
  };

  const handleExitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedSessionIds(new Set());
  };

  const handleToggleSelect = (id: string) => {
    setSelectedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedSessionIds.size === sessions.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(sessions.map(s => s.id)));
    }
  };

  const selectedSessions: BulkSelectedSession[] = sessions
    .filter(s => selectedSessionIds.has(s.id))
    .map(s => ({ id: s.id, name: s.name, start: s.start, end: s.end }));

  const allOnPageSelected = sessions.length > 0 && selectedSessionIds.size === sessions.length;

  // ----- Build tile grid -----
  let containerContent = <></>;
  if (loading) {
    containerContent = (
      <Row className="d-flex align-content-center" style={{ height: "70vh" }}>
        <Col className="d-flex justify-content-center">
          <Spinner animation="border" variant="light" />
        </Col>
      </Row>
    );
  } else {
    containerContent = (
      <>
        <Row className="px-1">
          {sessions.map((session) => (
            <Col xs={12} sm={4} md={3} lg={3} className="p-1" key={session.id}>
              <SessionTile
                id={session.id}
                name={session.name}
                date={session.date}
                sensorId={session.sensorId}
                icon={session.icon}
                isSelectable={isSelectMode}
                isSelected={selectedSessionIds.has(session.id)}
                onToggleSelect={handleToggleSelect}
              />
            </Col>
          ))}
        </Row>
        <Row className="pt-5 pb-5">
          <Col className="d-flex justify-content-center">
            <SessionPagination
              activePage={currentPage}
              sessionsPerPage={SESSIONS_PER_PAGE}
              totalSessions={totalCount}
              onPageChange={handlePageChange}
            />
          </Col>
        </Row>
      </>
    );
  }

  if (recordings.length === 0 && !loading) {
    containerContent = (
      <Row className="d-flex align-content-center" style={{ height: "70vh" }}>
        <Col className="d-flex justify-content-center">
          <h1>No sessions found</h1>
        </Col>
      </Row>
    );
  }

  return (
    <Container fluid className="background-color text-white p-4">
      <Row>
        <Col>
          <CustomBreadcrumbs
            path={[{ path: "/dashboard", title: "Dashboard" }]}
            page="Sessions"
          />
        </Col>
      </Row>

      <Row className="pt-4">
        <Col>
          <h1>Sessions</h1>
        </Col>
        <Col className="d-flex justify-content-end align-items-center">
          {!isSelectMode && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleEnterSelectMode}
              className="d-flex align-items-center gap-2"
              id="select-sessions-btn"
            >
              <BsCheckSquare />
              Select Sessions
            </Button>
          )}
        </Col>
      </Row>

      <Row>
        <Col>
          <span className="detail-page-subheading">
            Get detailed insights into the environmental conditions during a
            particular session.
          </span>
        </Col>
      </Row>

      <Row className="pt-3 pb-3">
        <Col className="d-flex justify-content-start">
          <Searchbar onSearch={handleSearch} />
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="pe-2">
            <CalendarButton
              onDateRangeChange={(startDate, endDate) => {
                handleFilterChange({ ...activeFilters, startDate, endDate });
              }}
            />
          </div>
          <div className="pe-2">
            <FilterButton onFilterChange={handleFilterChange} />
          </div>
        </Col>
      </Row>

      {/* Selection toolbar */}
      {isSelectMode && (
        <div className="sessions-selection-toolbar d-flex align-items-center gap-3 mb-3 px-3 py-2 rounded">
          <input
            type="checkbox"
            id="select-all-sessions-checkbox"
            checked={allOnPageSelected}
            onChange={handleSelectAll}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
            aria-label="Select all sessions on this page"
          />
          <label htmlFor="select-all-sessions-checkbox" className="mb-0 small" style={{ cursor: 'pointer' }}>
            {allOnPageSelected ? 'Deselect All' : 'Select All'}
          </label>
          <span className="text-muted small ms-2">
            {selectedSessionIds.size} selected
          </span>
          <div className="ms-auto d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={selectedSessionIds.size === 0}
              onClick={() => setShowBulkModal(true)}
              className="d-flex align-items-center gap-2"
              id="download-selected-btn"
            >
              <BsDownload />
              Download Selected
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleExitSelectMode}
              className="d-flex align-items-center gap-1"
              id="cancel-select-btn"
            >
              <BsXLg />
              Cancel
            </Button>
          </div>
        </div>
      )}

      <Container fluid className="px-1">
        {containerContent}
      </Container>

      <BulkDownloadModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        sessions={selectedSessions}
      />
    </Container>
  );
}
